import { useId, useState, type FC } from 'react';
import type { SampleTestDto } from '@/api';
import { Alert, Button, Dialog, Input, Textarea } from '@/ui';

import { useScheduleSampleTest } from './api/mutations';
import { describeSampleTest } from './lib/describe';
import {
  canSubmitSchedule,
  createScheduleDraft,
  toScheduleBody,
} from './lib/draft';
import { resolveSampleTestErrorMessage } from './lib/errors';

/*
 * «Перенести тест»: дата теста и комментарий; статус не меняется. Перенесённая
 * дата за днём отгрузки больше не следует (решает сервер). Пустой комментарий
 * снимает прежний.
 */

export interface ScheduleSampleTestDialogProps {
  test: SampleTestDto;
  onClose: () => void;
}

export const ScheduleSampleTestDialog: FC<ScheduleSampleTestDialogProps> = ({
  test,
  onClose,
}) => {
  const dateId = useId();
  const commentId = useId();
  const [draft, setDraft] = useState(() => createScheduleDraft(test));
  const schedule = useScheduleSampleTest();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Перенести тест"
      description={describeSampleTest(test)}
      widthClassName="w-[min(480px,90vw)]"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            disabled={!canSubmitSchedule(draft) || schedule.isPending}
            onClick={() =>
              schedule.mutate(
                { path: { id: test.id }, body: toScheduleBody(draft) },
                { onSuccess: onClose },
              )
            }
          >
            Перенести
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={dateId} className="text-xs font-medium text-ink-700">
            Дата теста
          </label>
          <Input
            id={dateId}
            type="date"
            className="w-[160px]"
            value={draft.testDate}
            onChange={(event) =>
              setDraft({ ...draft, testDate: event.target.value })
            }
          />
          <span className="text-xs text-ink-500">
            Когда узнать у клиента результат. После переноса дата больше не
            следует за датой отгрузки.
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={commentId}
            className="text-xs font-medium text-ink-700"
          >
            Комментарий
          </label>
          <Textarea
            id={commentId}
            placeholder="Клиент запустит пробную партию после ремонта линии..."
            maxLength={1000}
            value={draft.comment}
            onChange={(event) =>
              setDraft({ ...draft, comment: event.target.value })
            }
          />
        </div>
      </div>
      {schedule.isError && (
        <Alert tone="error" className="mt-3.5">
          Не удалось перенести: {resolveSampleTestErrorMessage(schedule.error)}
        </Alert>
      )}
    </Dialog>
  );
};
